import { useState, useEffect } from 'react';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

type FallbackImageProps = {
  src: string | undefined;
  fallbackSrc: string | string[];
  alt: string;
  className?: string;
  width: number;
  height: number;
  fallbackWidth?: number;
  fallbackHeight?: number;
  fallbackClassName?: string;
};

export default function FallbackImage({
  src,
  fallbackSrc,
  alt,
  className,
  width,
  height,
  fallbackWidth,
  fallbackHeight,
  fallbackClassName,
}: FallbackImageProps) {
  // Convert fallbackSrc to array if it's a string
  const fallbackSources = Array.isArray(fallbackSrc) ? fallbackSrc : [fallbackSrc];
  const [currentSrcIndex, setCurrentSrcIndex] = useState(0);
  const [imgSrc, setImgSrc] = useState(src || fallbackSources[0]);
  const [usingFallback, setUsingFallback] = useState(false);

  // Reset state when src or fallbackSrc changes
  useEffect(() => {
    setImgSrc(src || fallbackSources[0]);
    setCurrentSrcIndex(0);
    setUsingFallback(false);
  }, [src, fallbackSrc]);

  const handleError = () => {
    if (src && currentSrcIndex === 0) {
      // Main src failed, try first fallback
      setImgSrc(fallbackSources[0]);
      setCurrentSrcIndex(1);
      setUsingFallback(true);
    } else if (currentSrcIndex < fallbackSources.length) {
      // Try next fallback source
      setImgSrc(fallbackSources[currentSrcIndex]);
      setCurrentSrcIndex(currentSrcIndex + 1);
    }
    // If all sources fail, the last one will remain
  };

  return (
    <Image
      src={imgSrc}
      alt={alt}
      className={usingFallback ? fallbackClassName || className : className}
      width={usingFallback ? fallbackWidth || width : width}
      height={usingFallback ? fallbackHeight || height : height}
      onError={handleError}
    />
  );
}
